'use client'

import Charts from '@/components/charts'
import EnterView from '@/components/enterView'
import { useGlobalValue } from '@/lib/store/useGlobal'
import ImgPanel1 from '@image/reach/panel1.png'
import ImgPanel2 from '@image/reach/panel2.png'
import ImgPanel3 from '@image/reach/panel3.png'
import ImgLocation from '@image/reach/location.png'
import Image from 'next/image'
import { ECBasicOption } from 'echarts/types/src/util/types.js'
import offshoreData from './offshore.data.json'

export default function Traffic() {
  return (
    <section className='section mt-7.5 lg:mt-21.5'>
      <EnterView skeleton={<div className='h-126.5 lg:h-70 skeleton'></div>}>
        <Chart />
      </EnterView>
    </section>
  )
}

function Chart() {
  const { lg } = useGlobalValue()
  return (
    <div className='flex flex-col lg:flex-row gap-y-7.5 gap-x-15'>
      <aside className='shrink-0'>
        <div className='h-70 w-75 relative pr-8 mx-auto max-w-full'>
          <Charts className='h-full w-full' options={offshoreData} />
          <span className='absolute right-0 top-1/2 -translate-y-18 w-18.75 h-18.75 bg-blue-dark text-white animate-fade-in rounded-full grid place-items-center text-center text-sm'>
            offshore
            <br />
            25%
          </span>
          <Image
            src={ImgLocation}
            unoptimized
            alt='location'
            className='absolute right-2 top-0 w-12.5'
          />
        </div>
      </aside>
      <div className='flex-1'>
        <strong className='text-center text-xl block'>
          Overlaying NZ & AU showing 75 % local vs 25 % offshore traffic.
        </strong>
        <div className='grid grid-cols-3 gap-x-4 lg:gap-x-5'>
          <div>
            <Charts
              className='h-25 lg:h-40 w-full mx-auto'
              options={getOptions(80, ImgPanel1.src, lg)}
            />
            <p className='text-font-primary/70 mt-5 text-center line-clamp-2'>
              Visitors from local New Zealand
            </p>
          </div>
          <div>
            <Charts
              className='h-25 lg:h-40 w-full mx-auto'
              options={getOptions(70, ImgPanel2.src, lg)}
            />
            <p className='text-font-primary/70 mt-5 text-center line-clamp-2'>
              Visitors from local Australia
            </p>
          </div>
          <div>
            <Charts
              className='h-25 lg:h-40 w-full mx-auto'
              options={getOptions(50, ImgPanel3.src, lg)}
            />
            <p className='text-font-primary/70 mt-5 text-center line-clamp-2'>
              VFrom Hongkong, China Taiwan, United State, Singapore...
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function getOptions(number: number, image: string, lg: boolean) {
  const _panelImageURL = image
  const _animationDuration = 1000
  const _animationDurationUpdate = 1000
  const _animationEasingUpdate = 'quarticInOut'
  const _valOnRadianMax = 200
  const _outerRadius = lg ? 80 : 50
  const _innerRadius = lg ? 60 : 35
  const _pointerInnerRadius = 10
  const _insidePanelRadius = lg ? 45 : 25
  function renderItem(params: any, api: any) {
    const valOnRadian = api.value(1)
    const coords = api.coord([api.value(0), valOnRadian])
    const polarEndRadian = coords[3]
    const imageStyle = {
      image: _panelImageURL,
      x: params.coordSys.cx - _outerRadius,
      y: params.coordSys.cy - _outerRadius,
      width: _outerRadius * 2,
      height: _outerRadius * 2,
    }
    return {
      type: 'group',
      children: [
        {
          type: 'image',
          style: imageStyle,
          clipPath: {
            type: 'sector',
            shape: {
              cx: params.coordSys.cx,
              cy: params.coordSys.cy,
              r: _outerRadius,
              r0: _innerRadius,
              startAngle: 0,
              endAngle: -polarEndRadian,
              transition: 'endAngle',
              enterFrom: { endAngle: 0 },
            },
          },
        },
        {
          type: 'image',
          style: imageStyle,
          clipPath: {
            type: 'polygon',
            shape: {
              points: makePionterPoints(params, polarEndRadian),
            },
            extra: {
              polarEndRadian: polarEndRadian,
              transition: 'polarEndRadian',
              enterFrom: { polarEndRadian: 0 },
            },
            during: function (apiDuring: any) {
              apiDuring.setShape(
                'points',
                makePionterPoints(params, apiDuring.getExtra('polarEndRadian'))
              )
            },
          },
        },
        {
          type: 'circle',
          shape: {
            cx: params.coordSys.cx,
            cy: params.coordSys.cy,
            r: _insidePanelRadius,
          },
          style: {
            fill: '#fff',
            shadowBlur: 25,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowColor: 'rgba(76,107,167,0.4)',
          },
        },
        {
          type: 'text',
          extra: {
            valOnRadian: valOnRadian,
            transition: 'valOnRadian',
            enterFrom: { valOnRadian: 0 },
          },
          style: {
            text: makeText(valOnRadian),
            fontSize: lg ? 26 : 14,
            fontWeight: 700,
            x: params.coordSys.cx,
            y: params.coordSys.cy,
            fill: 'rgb(0,50,190)',
            align: 'center',
            verticalAlign: 'middle',
            enterFrom: { opacity: 0 },
          },
          during: function (apiDuring: any) {
            apiDuring.setStyle(
              'text',
              makeText(apiDuring.getExtra('valOnRadian'))
            )
          },
        },
      ],
    }
  }
  function convertToPolarPoint(
    renderItemParams: any,
    radius: any,
    radian: any
  ) {
    return [
      Math.cos(radian) * radius + renderItemParams.coordSys.cx,
      -Math.sin(radian) * radius + renderItemParams.coordSys.cy,
    ]
  }
  function makePionterPoints(renderItemParams: any, polarEndRadian: any) {
    return [
      convertToPolarPoint(renderItemParams, _outerRadius, polarEndRadian),
      convertToPolarPoint(
        renderItemParams,
        _outerRadius,
        polarEndRadian + Math.PI * 0.03
      ),
      convertToPolarPoint(
        renderItemParams,
        _pointerInnerRadius,
        polarEndRadian
      ),
    ]
  }
  function makeText(valOnRadian: any) {
    // Validate additive animation calc.
    if (valOnRadian < -10) {
      alert('illegal during val: ' + valOnRadian)
    }
    return ((valOnRadian / _valOnRadianMax) * 100).toFixed(0) + '%'
  }
  return {
    animationEasing: _animationEasingUpdate,
    animationDuration: _animationDuration,
    animationDurationUpdate: _animationDurationUpdate,
    animationEasingUpdate: _animationEasingUpdate,
    dataset: {
      source: [[1, number]],
    },
    angleAxis: {
      type: 'value',
      startAngle: 0,
      show: false,
      min: 0,
      max: _valOnRadianMax,
    },
    radiusAxis: {
      type: 'value',
      show: false,
    },
    polar: {},
    series: [
      {
        type: 'custom',
        coordinateSystem: 'polar',
        renderItem: renderItem,
      },
    ],
  } as ECBasicOption
}
